import React, { useState, useEffect, useRef  } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Mic } from 'lucide-react';

const PatientFormModal = ({ isOpen, onClose, onSave, patient = null, tempPatient = false }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    consultationPrice: '',
    whatsappNumber: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isUserStopped, setIsUserStopped] = useState(false);
  const recognitionRef = useRef(null);
  const mediaStreamRef = useRef(null); 
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const buttonRef = useRef(null);

  // Reconhecimento de voz
  useEffect(() => {
    let SpeechRecognition;

    const setupRecognition = async () => {
      if (!isIOS) {
        // Verifica suporte
        SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
          return;
        }

        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.lang = 'pt-BR'; // Português do Brasil
        
        if (!isMobile) {
          recognitionRef.current.interimResults = true; 
          recognitionRef.current.continuous = true;
        }
        else {
          recognitionRef.current.interimResults = true; 
          recognitionRef.current.continuous = false;
        }

        recognitionRef.current.onend = () => {
          setIsListening(false);
        }

        recognitionRef.current.onresult = (event) => {
          let finalTranscript = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcriptPart = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcriptPart;
            }
          }
          setTranscript(finalTranscript);
        };

        recognitionRef.current.onerror = (event) => {
          console.error('Erro na transcrição:', event.error);
        };

      }
      else {
          // it is IOS.
          // Carrega o ponyfill só no iOS
          // Carregamento dinâmico só no iOS
          try {

            // Obtém o token para usar o serviço da Azure, passando a chave.
            const appUrl = import.meta.env.VITE_APP_URL;
            const res = await fetch(appUrl + '/api/speech-token');

            if (!res.ok) throw new Error('Falha ao obter token');
            const { token, region } = await res.json();

            // Import dinâmico do pacote principal (resolve o erro de specifier no Vite)
            const { createSpeechRecognitionPonyfill } = await import('web-speech-cognitive-services');

            const { SpeechRecognition } = createSpeechRecognitionPonyfill({
              credentials: {
                authorizationToken: token, 
                region: region, 
              },
            });

            if (!SpeechRecognition) {
              return;
            }

            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.lang = 'pt-BR'; // Português do Brasil
            
            if (!isMobile) {
              recognitionRef.current.interimResults = true; 
              recognitionRef.current.continuous = true;
            }
            else {
              recognitionRef.current.interimResults = true; 
              recognitionRef.current.continuous = false;
            }

            recognitionRef.current.onend = () => {
              setIsListening(false);
            }

            recognitionRef.current.onresult = (event) => {
              let interimTranscript = '';
              let finalTranscript = '';

              if (event.results[event.results.length - 1]?.isFinal)
              {
                finalTranscript = event.results[event.results.length - 1]?.[0].transcript;
                setTranscript(finalTranscript);
              }

              // Começa do índice mais recente (event.resultIndex) para eficiência
              for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcriptPart = event.results[i][0].transcript.trim(); // .trim() para limpar espaços extras

                if (event.results[i].isFinal) {
                  finalTranscript += transcriptPart + ' '; // Adiciona espaço para separar frases
                } else {
                  interimTranscript += transcriptPart;
                }
              }

            };

            recognitionRef.current.onerror = (event) => {
              console.error('Erro na transcrição:', event.error);
            };

          } catch (err) {
            console.error('Falha ao carregar ponyfill Azure para iOS:', err);
            return;
          }

      }
    }

    setupRecognition();

    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
    };            
    
  }, [isIOS]);

  const toggleListening = () => {
    if (isListening) {
      // Usuário quer parar manualmente
      setIsUserStopped(true);      
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      if (recognitionRef.current) {
          setIsUserStopped(false);
          recognitionRef.current.start();
          setIsListening(true);
          setTranscript(''); // opcional: limpa transcrição anterior
      }
    }
  };

  useEffect(() => {
    if (patient) {
      // Detect if whatsapp_number from database is international format
      const rawNumber = patient?.whatsappNumber || patient?.whatsapp_number || '';
      let formattedNumber = rawNumber.replace(/\D/g, '');;
      
      // If number has more than 11 digits, it's international (has country code)
      if (rawNumber?.length > 11) {
        // Add + prefix so validation recognizes it as international
        formattedNumber = `+${rawNumber}`;
      }
      const formatted = formatPhoneNumber(formattedNumber);

      setFormData(prev => ({
        ...prev,  // mantém todos os campos que já estavam no formData
        fullName: patient.fullName || patient?.full_name || '',
        consultationPrice: String(patient.consultationPrice || patient.consultation_price || ''),
        whatsappNumber: formatted || '',
      }));

    } else {
      setFormData({
        fullName: '',
        consultationPrice: '',
        whatsappNumber: '',
      });
    }
    setErrors({});
  }, [patient, isOpen]);

  // ESC key handler to close modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event?.key === 'Escape' && isOpen) {
        cleanupMicrophone();
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);


  const handleSpeech = (value) => {

    if (!value) {
      return "";
    }

    // Cleanings...
    let stringLimpa = value.replace(/\./g, '');
    
    if (!isNaN) {
       stringLimpa = stringLimpa.replace(/\s/g, '');;
    }

    const cleanSpeech = stringLimpa.replace(',', '.');
    const yetCleaner = cleanSpeech.replace(':', '.');
    const noBRL = yetCleaner.replace("r$","");
    const noSlash = noBRL.replace("/",".");
    let lastSpeech = noSlash.replace('-', '');
    
    
    // Caso seja numérico mas tenha vindo com outros caracteres no meio.
    if (!isNaN(lastSpeech)) {
      lastSpeech = lastSpeech.replace(/\s/g, '');  // Elimina espaços internos.
      lastSpeech = lastSpeech.replace(/-/g, '');   // Elimina hífens. 
    }

    // Caso não seja numérico mas tenha vindo com outros caracteres no meio.
    if (isNaN(lastSpeech)) {
      lastSpeech = lastSpeech.replace(/-/g, '');   // Elimina hífens. 
    }

    if (/^[\d\s]+$/.test(lastSpeech)) {
      // Se tiver letras ou outros caracteres, retorna a string original (ou pode retornar erro)
      lastSpeech = lastSpeech.replace(/\s/g, '.');
    }

    const speech = lastSpeech;

    let result = {
        type: "",
        text: ""
      }

    // Se for número e aparentemente telefone.
    if (!isNaN(speech) && (speech.length >= 7) ) {
      result = {
        type: "telefone",
        text: speech.replace('.', '') 
      }
      return result;
    }
    else if (isNaN(speech) && speech.includes('.')) {  // Se for número e aparentemente preço da consulta.
      result = {
        type: "preço",
        text: speech.replace('.', ',') 
      }
      return result;
    }
    else if (!isNaN(speech) && !speech.includes('.')) {  // Se for número e aparentemente preço da consulta.
      result = {
        type: "preço",
        text: speech.replace('.', ',') 
      }
      return result;
    }
    else if (isNaN(speech) && (speech.toLowerCase() !== 'adicionar') && (speech.toLowerCase() !== 'salvar') && (speech.toLowerCase() !== 'gravar') && (speech.toLowerCase() !== 'parar') ) { // Se não for número.
      result = {
        type: "nome",
        text: speech 
      }
      return result;
    }
    else if ( (speech.toLowerCase() === "adicionar") || (speech.toLowerCase() === "salvar") || (speech.toLowerCase() === "gravar"))  {
      result = {
        type: "command",
        text: "save" 
      }
    }
    else if (speech.toLowerCase() === "parar") {
      result = {
        type: "command",
        text: "stop" 
      }      
    }
    
    return result;
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters except + at the start
    let cleaned = value?.replace(/[^\d+]/g, '');
    
    // Ensure + is only at the start
    if (cleaned?.includes('+')) {
      const plusCount = (cleaned?.match(/\+/g) || [])?.length;
      if (plusCount > 1 || cleaned?.indexOf('+') !== 0) {
        cleaned = cleaned?.replace(/\+/g, '');
      }
    }
    
    // If it starts with +, format as international number
    if (cleaned?.startsWith('+')) {
      const numbers = cleaned?.slice(1); // Remove + for processing
      
      // +55 (11) 98765-4321 format
      if (numbers?.length <= 2) {
        return `+${numbers}`;
      } else if (numbers?.length <= 4) {
        return `+${numbers?.slice(0, 2)} (${numbers?.slice(2)})`;
      } else if (numbers?.length <= 9) {
        return `+${numbers?.slice(0, 2)} (${numbers?.slice(2, 4)}) ${numbers?.slice(4)}`;
      } else if (numbers?.length <= 13) {
        return `+${numbers?.slice(0, 2)} (${numbers?.slice(2, 4)}) ${numbers?.slice(4, 9)}-${numbers?.slice(9)}`;
      }
      return value;
    } else {
      // Fallback to local format (DDD) XXXXX-XXXX
      const numbers = cleaned;
      if (numbers?.length <= 2) return numbers;
      if (numbers?.length <= 7) return `(${numbers?.slice(0, 2)}) ${numbers?.slice(2)}`;
      if (numbers?.length <= 11) {
        return `(${numbers?.slice(0, 2)}) ${numbers?.slice(2, 7)}-${numbers?.slice(7)}`;
      }
      return value;
    }
  };

  const formatCurrency = (value) => {
    // Remove tudo exceto números e vírgula/ponto
    const numbers = value?.replace(/[^\d,]/g, '');
    return numbers;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e?.target?.value);
    setFormData(prev => ({ ...prev, whatsappNumber: formatted }));
    if (errors?.whatsappNumber) {
      setErrors(prev => ({ ...prev, whatsappNumber: '' }));
    }
  };

  const handlePriceChange = (e) => {
    const formatted = formatCurrency(e?.target?.value);
    setFormData(prev => ({ ...prev, consultationPrice: formatted }));
    if (errors?.consultationPrice) {
      setErrors(prev => ({ ...prev, consultationPrice: '' }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Convert to string and trim to safely check
    const fullName = String(formData?.fullName || '')?.trim();
    const consultationPrice = String(formData?.consultationPrice || '')?.trim();
    const whatsappNumber = String(formData?.whatsappNumber || '')?.trim();

    if (!fullName) {
      newErrors.fullName = 'Nome é obrigatório';
    }

    if (!consultationPrice) {
      newErrors.consultationPrice = 'Valor da consulta é obrigatório';
    } else {
      const price = parseFloat(consultationPrice?.replace(',', '.'));
      if (isNaN(price) || price <= 0) {
        newErrors.consultationPrice = 'Valor deve ser maior que zero';
      }
    }

    if (!whatsappNumber) {
      newErrors.whatsappNumber = 'Número de WhatsApp é obrigatório';
    } else {
      const numbers = whatsappNumber?.replace(/\D/g, '');
      
      if (whatsappNumber?.startsWith('+')) {
        // International format: Accept any valid international number
        // Minimum 10 digits (country code + number), maximum 15 digits (E.164 standard)
        if (numbers?.length < 10 || numbers?.length > 15) {
          newErrors.whatsappNumber = 'Formato internacional inválido. Use: +[código do país] (DDD) número';
        }
      } else {
        // Local format: Strict check for Brazilian format (11 digits)
        if (numbers?.length !== 11) {
          newErrors.whatsappNumber = 'Formato local deve ter 11 dígitos: (DDD) 9XXXX-XXXX';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    handlePhoneChange();

    if (!validateForm()) return;

    setIsLoading(true);

    // Map form data to database schema (camelCase -> snake_case)
    const patientData = {
      full_name: formData?.fullName,
      consultation_price: parseFloat(formData?.consultationPrice?.replace(',', '.')),
      whatsapp_number: formData?.whatsappNumber?.replace(/\D/g, ''),
      phone: formData?.whatsappNumber?.replace(/\D/g, ''), // Use WhatsApp as phone for backward compatibility
      status: 'active', // Default status for new patients
    };

    const result = await onSave(patientData);
    setIsLoading(false);
    
    if (result?.success !== false) {
        cleanupMicrophone();    
        onClose();
    } else {
      // Show error if provided
      if (result?.error) {
        setErrors({ submit: result?.error });
      }
    }
  };

  if (!isOpen) return null;

  
// Dedicated cleanup function — call this also on manual stop if needed
const cleanupMicrophone = () => {
  if (recognitionRef.current) {
    recognitionRef.current.stop();
    recognitionRef.current.abort(); // Forces immediate termination
    recognitionRef.current = null;
  }

  // Crucial: stop all media tracks to release the microphone
  if (mediaStreamRef.current) {
    mediaStreamRef.current.getTracks().forEach(track => {
      track.stop(); // This releases the mic hardware
    });
    mediaStreamRef.current = null;
  }
};


// Quando a transcrição detectar preço:
const speechResult = handleSpeech(transcript.trim());
useEffect(() => {
  if (transcript) {
    
    if (speechResult?.type === "preço") {
      setFormData(prev => ({
        ...prev,
        consultationPrice: speechResult.text
      }));
    }
    
    // Se detectar outro tipo (ex: fullName)
    if (speechResult?.type === "nome") {
      setFormData(prev => ({
        ...prev,
        fullName: speechResult.text
      }));
    }

    // Se detectar outro tipo (ex: whatsappNumber)
    if (speechResult?.type === "telefone") {
      setFormData(prev => ({
        ...prev,
        whatsappNumber: speechResult.text
      }));
    }

    if (speechResult?.type === "command" && speechResult?.text === "save") {
      // Dispara o submit do formulário
      handleSubmit();
    }

    if (speechResult?.type === "command" && speechResult?.text === "stop") {
      // Dispara o submit do formulário
      toggleListening();


      // Opcional: limpa o transcript após o comando (para evitar repetir)
      setTranscript('');
    }

  }
}, [transcript]);

const startListening = () => {
  const recognition = recognitionRef.current;
  if (!recognition) return;

  // Se já estiver ouvindo, não faz nada (ou força restart)
  if (isListening) {
    recognition.stop(); // limpa qualquer sessão pendente
  }

  try {
    recognition.start();
    setIsListening(true);
  } catch (error) {
    // Ignora o erro "already started" – é comum em hold-to-talk rápido
    if (error.name !== 'InvalidStateError') {
      console.error('Erro ao iniciar reconhecimento:', error);
    }
  }
};

const stopListening = () => {
  recognitionRef.current?.stop(); // mais rápido, cancela sem esperar resultado
  setIsListening(false);
};

  useEffect(() => {
    const button = buttonRef.current;
    if (!button || !isMobile) return;

    const handleTouchStart = (e) => {
      e.preventDefault(); // Agora funciona!
      startListening();
    };

    const handleTouchEnd = (e) => {
      e.preventDefault();
      stopListening();
    };

    // Aqui está a mágica: { passive: false }
    button.addEventListener('touchstart', handleTouchStart, { passive: false });
    button.addEventListener('touchend', handleTouchEnd, { passive: false });
    button.addEventListener('touchcancel', stopListening, { passive: false });

    return () => {
      button.removeEventListener('touchstart', handleTouchStart);
      button.removeEventListener('touchend', handleTouchEnd);
      button.removeEventListener('touchcancel', stopListening);
    };
  }, [isMobile]); // Dependências mínimas

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-2xl border border-border w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {tempPatient ? 'Paciente Novo' : (patient ? 'Editar Paciente' : 'Novo Paciente')}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            <Input
              label="Nome"
              type="text"
              name="fullName"
              placeholder="Maria Silva Santos"
              value={formData?.fullName}
              onChange={handleInputChange}
              error={errors?.fullName}
              required
            />

            <Input
              label="Valor da Consulta (R$)"
              type="text"
              name="consultationPrice"
              placeholder="150,00"
              value={formData?.consultationPrice}
              onChange={handlePriceChange}
              error={errors?.consultationPrice}
              description="Digite o valor em Reais (ex: 150,00)"
              required
            />

            <Input
              label="Número de WhatsApp"
              type="tel"
              name="whatsappNumber"
              placeholder="+55 (11) 98765-4321"
              value={formData?.whatsappNumber}
              onChange={handlePhoneChange}
              error={errors?.whatsappNumber}
              description="Formatos: +55 (DDD) 9XXXX-XXXX ou (DDD) 9XXXX-XXXX"
              required
            />

            <p className={`${isListening ? '' : 'invisible'} p-2 !mt-0.5 text-center`}>Fale cada campo separadamente: Nome, valor e telefone. Por último, diga "gravar"</p>
            <p className={`${!SpeechRecognition ? '' : 'invisible'} text-center !-mt-4`}>Seu navegador não suporta transcrição de voz</p>

            {errors?.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{errors?.submit}</p>
              </div>
            )}
          </div>
        </form>

        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          {/* Lado esquerdo: botões de ação */}
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Retornar
            </Button>
            <Button
              variant="default"
              onClick={handleSubmit}
              loading={isLoading}
              iconName="Check"
              iconPosition="right"
            >
              {patient ? 'Salvar Alterações' : 'Adicionar'}
            </Button>
          </div>

          {/* Lado direito: botão de microfone (flutuante, mas na mesma linha) */}
          {isMobile ? (
            // Mobile
            <Button
              ref={buttonRef}
              onMouseDown={isMobile ? startListening : undefined}
              onMouseUp={isMobile ? stopListening : undefined}
              onMouseLeave={isMobile ? stopListening : undefined}              
              className="-mr-2 w-14 h-14 bg-blue-600 hover:bg-indigo-700 text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
              aria-label={isListening ? 'Parar transcrição' : 'Iniciar transcrição'}
              disabled={!SpeechRecognition ? true : false}
            >
              <Mic className="w-6 h-6" />
              {/* Opcional: texto só aparece quando não está gravando, se quiser manter o comportamento antigo */}
              {isListening ? 'Parar' : ''}
            </Button>
          ) : (
            // Desktop
            <Button
              onClick={toggleListening}
              className="ml-4 w-14 h-14 bg-blue-600 hover:bg-indigo-700 text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
              aria-label={isListening ? 'Parar transcrição' : 'Iniciar transcrição'}
              disabled={!SpeechRecognition ? true : false}
            >
              <Mic className="w-6 h-6" />
              {/* Opcional: texto só aparece quando não está gravando, se quiser manter o comportamento antigo */}
              {isListening ? 'Parar' : ''}
            </Button>
          )}

        </div>

      </div>
    </div>
  );
};

export default PatientFormModal;